import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  getCollections,
  exportData,
  importData,
  getCollectionCount,
  clearDataOperations
} from '../../actions/dataImportExportActions';
import { getPrograms } from '../../actions/programActions';
import axios from 'axios';
import { tokenConfig } from '../../actions/authActions';
import { 
  Download, 
  Upload, 
  Database, 
  FileText, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Loader,
  Info,
  FileDown,
  Filter,
  UserCheck,
  Calendar,
  X,
  FolderArchive
} from 'lucide-react';

const ImportExportDataTab = ({
  dataImportExport: {
    collections,
    collectionCounts,
    exportLoading,
    importLoading,
    collectionsLoading,
    lastExportInfo,
    lastImportResult,
    error,
    message
  },
  program: { programs, loading: programsLoading },
  auth,
  getCollections,
  exportData,
  importData,
  getCollectionCount,
  clearDataOperations,
  getPrograms
}) => {
  const [activeTab, setActiveTab] = useState('export');
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('add');
  const [targetCollections, setTargetCollections] = useState('');
  
  // Export CSV states
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedProgramCSV, setSelectedProgramCSV] = useState('');
  const [availableSubmissionFormsCSV, setAvailableSubmissionFormsCSV] = useState([]);
  const [selectedSubmissionFormsCSV, setSelectedSubmissionFormsCSV] = useState([]);
  const [loadingFormsCSV, setLoadingFormsCSV] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [exportingFiles, setExportingFiles] = useState(false);
  const [errorCSV, setErrorCSV] = useState(null);
  const [successMessageCSV, setSuccessMessageCSV] = useState(null);
  
  // Date range states for CSV export
  const [dateRanges, setDateRanges] = useState([]);
  const [selectedDateField, setSelectedDateField] = useState('');
  
  // Export Users CSV states
  const [selectedStatusUsers, setSelectedStatusUsers] = useState('all');
  const [selectedProgramUsers, setSelectedProgramUsers] = useState('');
  const [availableSubmissionFormsUsers, setAvailableSubmissionFormsUsers] = useState([]);
  const [selectedSubmissionFormsUsers, setSelectedSubmissionFormsUsers] = useState([]);
  const [loadingFormsUsers, setLoadingFormsUsers] = useState(false);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [errorUsers, setErrorUsers] = useState(null);
  const [successMessageUsers, setSuccessMessageUsers] = useState(null);

  useEffect(() => {
    getCollections();
    getPrograms();
    return () => {
      clearDataOperations();
    };
  }, []);

  // Get counts for all collections when they load
  useEffect(() => {
    if (collections.length > 0) {
      collections.forEach(collection => {
        if (!collectionCounts[collection.id]) {
          getCollectionCount(collection.id);
        }
      });
    }
  }, [collections.length]);

  const handleCollectionToggle = (collectionId) => {
    setSelectedCollections(prev => {
      if (prev.includes(collectionId)) {
        return prev.filter(id => id !== collectionId);
      } else {
        return [...prev, collectionId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCollections([]);
    } else {
      setSelectedCollections(collections.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleExport = async () => {
    if (selectedCollections.length === 0) {
      alert('Please select at least one collection to export');
      return;
    }

    const exportConfig = {
      collections: selectedCollections,
      exportType: 'full'
    };

    try {
      await exportData(exportConfig);
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/json') {
      setImportFile(file);
    } else {
      alert('Please select a valid JSON file');
      event.target.value = '';
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a JSON file to import');
      return;
    }

    try {
      // Read file content
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(importFile);
      });

      // Parse JSON
      let parsedData;
      try {
        parsedData = JSON.parse(fileContent);
      } catch (parseError) {
        alert('Invalid JSON file format');
        return;
      }

      // Prepare import config
      const importConfig = {
        importData: parsedData,
        mode: importMode,
        targetCollections: targetCollections.trim() ? 
          targetCollections.split(',').map(c => c.trim()) : 
          undefined
      };

      await importData(importConfig);
      setImportFile(null);
      document.getElementById('import-file').value = '';
      
      // Refresh collection counts
      collections.forEach(collection => {
        getCollectionCount(collection.id);
      });
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import data: ' + error.message);
    }
  };

  const getTotalDocuments = () => {
    return Object.values(collectionCounts).reduce((sum, count) => sum + count, 0);
  };

  // Export CSV Handlers
  const fetchSubmissionFormsForProgramCSV = async (programId) => {
    setLoadingFormsCSV(true);
    setErrorCSV(null);
    try {
      const config = tokenConfig(() => ({ auth }));
      const programResponse = await axios.get(`/api/programs/${programId}`, config);
      const program = programResponse.data;
      
      const allFormIds = new Set();
      if (program.submissionForms && program.submissionForms.length > 0) {
        program.submissionForms.forEach(sf => {
          const formId = typeof sf === 'string' ? sf : sf._id;
          allFormIds.add(formId);
        });
      }
      
      if (program.paths && program.paths.length > 0) {
        for (const pathRef of program.paths) {
          const pathId = typeof pathRef === 'string' ? pathRef : pathRef._id;
          try {
            const pathResponse = await axios.get(`/api/paths/${pathId}`, config);
            const path = pathResponse.data;
            
            if (path.courses && path.courses.length > 0) {
              for (const courseRef of path.courses) {
                const courseId = typeof courseRef === 'string' ? courseRef : courseRef._id;
                try {
                  const courseResponse = await axios.get(`/api/courses/${courseId}`, config);
                  const course = courseResponse.data;
                  
                  if (course.submissionForms && course.submissionForms.length > 0) {
                    course.submissionForms.forEach(sf => {
                      const formId = typeof sf === 'string' ? sf : sf._id;
                      allFormIds.add(formId);
                    });
                  }
                } catch (courseErr) {
                  console.warn(`Failed to fetch course ${courseId}:`, courseErr);
                }
              }
            }
          } catch (pathErr) {
            console.warn(`Failed to fetch path ${pathId}:`, pathErr);
          }
        }
      }
      
      if (allFormIds.size > 0) {
        const response = await axios.get('/api/submissionForms', config);
        const allForms = response.data;
        const programForms = allForms.filter(form => allFormIds.has(form._id) && form.isActive);
        setAvailableSubmissionFormsCSV(programForms);
        setSelectedSubmissionFormsCSV(programForms.map(form => form._id));
      } else {
        setAvailableSubmissionFormsCSV([]);
        setSelectedSubmissionFormsCSV([]);
      }
    } catch (err) {
      console.error('Error fetching submission forms:', err);
      setErrorCSV('Failed to load submission forms for the selected program');
      setAvailableSubmissionFormsCSV([]);
      setSelectedSubmissionFormsCSV([]);
    } finally {
      setLoadingFormsCSV(false);
    }
  };

  const fetchSubmissionFormsForProgramUsers = async (programId) => {
    setLoadingFormsUsers(true);
    setErrorUsers(null);
    try {
      const config = tokenConfig(() => ({ auth }));
      const programResponse = await axios.get(`/api/programs/${programId}`, config);
      const program = programResponse.data;
      
      const allFormIds = new Set();
      if (program.submissionForms && program.submissionForms.length > 0) {
        program.submissionForms.forEach(sf => {
          const formId = typeof sf === 'string' ? sf : sf._id;
          allFormIds.add(formId);
        });
      }
      
      if (program.paths && program.paths.length > 0) {
        for (const pathRef of program.paths) {
          const pathId = typeof pathRef === 'string' ? pathRef : pathRef._id;
          try {
            const pathResponse = await axios.get(`/api/paths/${pathId}`, config);
            const path = pathResponse.data;
            
            if (path.courses && path.courses.length > 0) {
              for (const courseRef of path.courses) {
                const courseId = typeof courseRef === 'string' ? courseRef : courseRef._id;
                try {
                  const courseResponse = await axios.get(`/api/courses/${courseId}`, config);
                  const course = courseResponse.data;
                  
                  if (course.submissionForms && course.submissionForms.length > 0) {
                    course.submissionForms.forEach(sf => {
                      const formId = typeof sf === 'string' ? sf : sf._id;
                      allFormIds.add(formId);
                    });
                  }
                } catch (courseErr) {
                  console.warn(`Failed to fetch course ${courseId}:`, courseErr);
                }
              }
            }
          } catch (pathErr) {
            console.warn(`Failed to fetch path ${pathId}:`, pathErr);
          }
        }
      }
      
      if (allFormIds.size > 0) {
        const response = await axios.get('/api/submissionForms', config);
        const allForms = response.data;
        const programForms = allForms.filter(form => allFormIds.has(form._id) && form.isActive);
        setAvailableSubmissionFormsUsers(programForms);
        setSelectedSubmissionFormsUsers(programForms.map(form => form._id));
      } else {
        setAvailableSubmissionFormsUsers([]);
        setSelectedSubmissionFormsUsers([]);
      }
    } catch (err) {
      console.error('Error fetching submission forms:', err);
      setErrorUsers('Failed to load submission forms for the selected program');
      setAvailableSubmissionFormsUsers([]);
      setSelectedSubmissionFormsUsers([]);
    } finally {
      setLoadingFormsUsers(false);
    }
  };

  useEffect(() => {
    if (selectedProgramCSV) {
      fetchSubmissionFormsForProgramCSV(selectedProgramCSV);
    } else {
      setAvailableSubmissionFormsCSV([]);
      setSelectedSubmissionFormsCSV([]);
    }
  }, [selectedProgramCSV]);

  useEffect(() => {
    if (selectedProgramUsers) {
      fetchSubmissionFormsForProgramUsers(selectedProgramUsers);
    } else {
      setAvailableSubmissionFormsUsers([]);
      setSelectedSubmissionFormsUsers([]);
    }
  }, [selectedProgramUsers]);

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setErrorCSV(null);
    setSuccessMessageCSV(null);
  };

  const handleStatusChangeUsers = (status) => {
    setSelectedStatusUsers(status);
    setErrorUsers(null);
    setSuccessMessageUsers(null);
  };

  const handleDateRangeChange = (index, field, value) => {
    setDateRanges(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDateRangeToggle = (index) => {
    setDateRanges(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], enabled: !updated[index].enabled };
      return updated;
    });
  };

  const availableDateFields = [
    { field: 'submittedAt', label: 'Submitted At' },
    { field: 'reviewedAt', label: 'Reviewed At' },
    { field: 'createdAt', label: 'Created At' },
    { field: 'updatedAt', label: 'Updated At' }
  ];

  const getUnusedDateFields = () => {
    const usedFields = dateRanges.map(dr => dr.field);
    return availableDateFields.filter(f => !usedFields.includes(f.field));
  };

  const handleAddDateRange = () => {
    if (!selectedDateField) return;
    
    const fieldInfo = availableDateFields.find(f => f.field === selectedDateField);
    if (fieldInfo) {
      setDateRanges(prev => [...prev, { 
        field: fieldInfo.field, 
        label: fieldInfo.label, 
        from: '', 
        to: '', 
        enabled: true 
      }]);
      setSelectedDateField('');
    }
  };

  const handleRemoveDateRange = (index) => {
    setDateRanges(prev => prev.filter((_, i) => i !== index));
  };

  const handleProgramChangeCSV = (e) => {
    setSelectedProgramCSV(e.target.value);
    setErrorCSV(null);
    setSuccessMessageCSV(null);
  };

  const handleProgramChangeUsers = (e) => {
    setSelectedProgramUsers(e.target.value);
    setErrorUsers(null);
    setSuccessMessageUsers(null);
  };

  const handleSubmissionFormToggleCSV = (formId) => {
    setSelectedSubmissionFormsCSV(prev => {
      if (prev.includes(formId)) {
        return prev.filter(id => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSubmissionFormToggleUsers = (formId) => {
    setSelectedSubmissionFormsUsers(prev => {
      if (prev.includes(formId)) {
        return prev.filter(id => id !== formId);
      } else {
        return [...prev, formId];
      }
    });
  };

  const handleSelectAllFormsCSV = () => {
    if (selectedSubmissionFormsCSV.length === availableSubmissionFormsCSV.length) {
      setSelectedSubmissionFormsCSV([]);
    } else {
      setSelectedSubmissionFormsCSV(availableSubmissionFormsCSV.map(form => form._id));
    }
  };

  const handleSelectAllFormsUsers = () => {
    if (selectedSubmissionFormsUsers.length === availableSubmissionFormsUsers.length) {
      setSelectedSubmissionFormsUsers([]);
    } else {
      setSelectedSubmissionFormsUsers(availableSubmissionFormsUsers.map(form => form._id));
    }
  };

  const handleExportCSV = async () => {
    setExportingCSV(true);
    setErrorCSV(null);
    setSuccessMessageCSV(null);

    try {
      const config = tokenConfig(() => ({ auth }));
      const params = new URLSearchParams();
      
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      if (selectedProgramCSV) {
        params.append('program', selectedProgramCSV);
      }
      
      if (selectedSubmissionFormsCSV.length > 0) {
        selectedSubmissionFormsCSV.forEach(formId => {
          params.append('formId', formId);
        });
      }
      
      // Add date range filters
      dateRanges.forEach(range => {
        if (range.enabled) {
          if (range.from) {
            params.append(`${range.field}From`, range.from);
          }
          if (range.to) {
            params.append(`${range.field}To`, range.to);
          }
        }
      });
      
      const response = await axios.get(
        `/api/submissions/export/csv?${params.toString()}`,
        {
          ...config,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const statusPart = selectedStatus !== 'all' ? `_${selectedStatus}` : '';
      const programPart = selectedProgramCSV ? `_program` : '';
      link.setAttribute('download', `submissions${statusPart}${programPart}_${timestamp}.csv`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessageCSV('CSV file exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      setErrorCSV(err.response?.data?.error || 'Failed to export CSV file');
    } finally {
      setExportingCSV(false);
    }
  };

  const handleExportFiles = async () => {
    setExportingFiles(true);
    setErrorCSV(null);
    setSuccessMessageCSV(null);

    try {
      const config = tokenConfig(() => ({ auth }));
      const params = new URLSearchParams();
      
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      if (selectedProgramCSV) {
        params.append('program', selectedProgramCSV);
      }
      
      if (selectedSubmissionFormsCSV.length > 0) {
        selectedSubmissionFormsCSV.forEach(formId => {
          params.append('formId', formId);
        });
      }
      
      // Add date range filters
      dateRanges.forEach(range => {
        if (range.enabled) {
          if (range.from) {
            params.append(`${range.field}From`, range.from);
          }
          if (range.to) {
            params.append(`${range.field}To`, range.to);
          }
        }
      });
      
      const response = await axios.get(
        `/api/submissions/export/files?${params.toString()}`,
        {
          ...config,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const statusPart = selectedStatus !== 'all' ? `_${selectedStatus}` : '';
      const programPart = selectedProgramCSV ? '_program' : '';
      link.setAttribute('download', `submission_images${statusPart}${programPart}_${timestamp}.zip`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessageCSV('Files exported successfully!');
    } catch (err) {
      console.error('Export files error:', err);
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          setErrorCSV(json.error || 'Failed to export files');
        } catch {
          setErrorCSV('Failed to export files');
        }
      } else {
        setErrorCSV(err.response?.data?.error || 'Failed to export files');
      }
    } finally {
      setExportingFiles(false);
    }
  };

  const handleExportUsers = async () => {
    if (!selectedProgramUsers) {
      setErrorUsers('Please select a program');
      return;
    }

    if (selectedSubmissionFormsUsers.length === 0) {
      setErrorUsers('Please select at least one submission form');
      return;
    }

    setExportingUsers(true);
    setErrorUsers(null);
    setSuccessMessageUsers(null);

    try {
      const config = tokenConfig(() => ({ auth }));
      const params = new URLSearchParams();
      params.append('program', selectedProgramUsers);
      
      if (selectedStatusUsers !== 'all') {
        params.append('status', selectedStatusUsers);
      }
      
      selectedSubmissionFormsUsers.forEach(formId => {
        params.append('formId', formId);
      });
      
      const response = await axios.get(
        `/api/users/export/csv?${params.toString()}`,
        {
          ...config,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().split('T')[0];
      const programName = programs.find(p => p._id === selectedProgramUsers)?.title || 'program';
      const statusPartUsers = selectedStatusUsers !== 'all' ? `_${selectedStatusUsers}` : '';
      link.setAttribute('download', `users_${programName}${statusPartUsers}_${timestamp}.csv`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccessMessageUsers('CSV file exported successfully!');
    } catch (err) {
      console.error('Export error:', err);
      setErrorUsers(err.response?.data?.error || 'Failed to export CSV file');
    } finally {
      setExportingUsers(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#054581] mb-2">Import/Export Data</h2>
        <p className="text-gray-600">
          Manage your database by importing and exporting collections data
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        <button
          className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
            activeTab === 'export'
              ? 'border-[#1086a4] text-[#1086a4]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('export')}
        >
          <Download className="inline-block w-4 h-4 mr-2" />
          Export Data
        </button>
        <button
          className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
            activeTab === 'import'
              ? 'border-[#1086a4] text-[#1086a4]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('import')}
        >
          <Upload className="inline-block w-4 h-4 mr-2" />
          Import Data
        </button>
        <button
          className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
            activeTab === 'exportCSV'
              ? 'border-[#1086a4] text-[#1086a4]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('exportCSV')}
        >
          <FileDown className="inline-block w-4 h-4 mr-2" />
          Export Submissions CSV
        </button>
        <button
          className={`px-6 py-3 font-medium text-sm border-b-2 whitespace-nowrap ${
            activeTab === 'exportUsers'
              ? 'border-[#1086a4] text-[#1086a4]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('exportUsers')}
        >
          <UserCheck className="inline-block w-4 h-4 mr-2" />
          Export Users CSV
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <XCircle className="w-5 h-5 text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {message && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
          <span className="text-green-700">{message}</span>
        </div>
      )}

      {/* Collections Overview */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center mb-2">
          <Database className="w-5 h-5 text-blue-500 mr-2" />
          <h3 className="font-medium text-blue-900">Database Overview</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Collections:</span>
            <span className="ml-2 font-medium">{collections.length}</span>
          </div>
          <div>
            <span className="text-gray-600">Total Documents:</span>
            <span className="ml-2 font-medium">{getTotalDocuments()}</span>
          </div>
        </div>
      </div>

      {activeTab === 'export' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Collections to Export</h3>
            
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Select All Collections</span>
              </label>
            </div>

            {collectionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader className="w-6 h-6 animate-spin text-[#1086a4]" />
                <span className="ml-2 text-gray-600">Loading collections...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {collections.map((collection) => (
                  <div key={collection.id} className="border border-gray-200 rounded-lg p-4">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        checked={selectedCollections.includes(collection.id)}
                        onChange={() => handleCollectionToggle(collection.id)}
                        className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">{collection.name}</div>
                        <div className="text-sm text-gray-500">
                          Documents: {collectionCounts[collection.id] || 'Loading...'}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedCollections.length} collection(s) selected
              </div>
              <button
                onClick={handleExport}
                disabled={exportLoading || selectedCollections.length === 0}
                className="inline-flex items-center px-4 py-2 bg-[#1086a4] text-white font-medium rounded-lg hover:bg-[#0c6b7a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportLoading ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export Selected
              </button>
            </div>
          </div>

          {lastExportInfo && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                <h4 className="font-medium text-green-900">Last Export Information</h4>
              </div>
              <div className="text-sm text-green-700 space-y-1">
                <div>Exported: {new Date(lastExportInfo.exportedAt).toLocaleString()}</div>
                <div>Collections: {lastExportInfo.collections.join(', ')}</div>
                <div>Total Documents: {lastExportInfo.totalDocuments}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'import' && (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Import Data from JSON File</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select JSON File
                </label>
                <input
                  id="import-file"
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-[#1086a4] file:text-white hover:file:bg-[#0c6b7a]"
                />
                {importFile && (
                  <div className="mt-2 text-sm text-green-600 flex items-center">
                    <FileText className="w-4 h-4 mr-1" />
                    Selected: {importFile.name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Import Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="add"
                      checked={importMode === 'add'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Add (preserve existing data)
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Collections (optional)
                </label>
                <input
                  type="text"
                  value={targetCollections}
                  onChange={(e) => setTargetCollections(e.target.value)}
                  placeholder="e.g., users, courses, groups (leave empty for all)"
                  className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4] sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Comma-separated collection names. Leave empty to import all collections from file.
                </p>
              </div>

              {importMode === 'erase' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">Warning: Erase Mode</p>
                      <p className="text-yellow-700 mt-1">
                        This will permanently delete all existing data in the selected collections before importing. 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6">
              <button
                onClick={handleImport}
                disabled={importLoading || !importFile}
                className="inline-flex items-center px-4 py-2 bg-[#1086a4] text-white font-medium rounded-lg hover:bg-[#0c6b7a] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importLoading ? (
                  <Loader className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Import Data
              </button>
            </div>
          </div>

          {lastImportResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Info className="w-5 h-5 text-blue-500 mr-2" />
                <h4 className="font-medium text-blue-900">Import Results</h4>
              </div>
              <div className="text-sm text-blue-700">
                <div className="grid grid-cols-3 gap-4 mb-3">
                  <div>
                    <span className="font-medium">Processed:</span> {lastImportResult.results.summary.totalProcessed}
                  </div>
                  <div>
                    <span className="font-medium">Success:</span> {lastImportResult.results.summary.totalSuccess}
                  </div>
                  <div>
                    <span className="font-medium">Errors:</span> {lastImportResult.results.summary.totalErrors}
                  </div>
                </div>
                <div>
                  <span className="font-medium">Mode:</span> {lastImportResult.mode}
                </div>
              </div>

              {Object.entries(lastImportResult.results.imported).map(([collection, result]) => (
                <div key={collection} className="mt-3 p-3 bg-white rounded border">
                  <div className="font-medium text-blue-900">{collection}:</div>
                  <div className="text-sm text-blue-700 mt-1">
                    Processed: {result.processed}, Success: {result.success}, 
                    Errors: {result.errors.length}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'exportCSV' && (
        <div className="space-y-6">
          {errorCSV && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
              <span className="text-red-700">{errorCSV}</span>
            </div>
          )}

          {successMessageCSV && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-green-700">{successMessageCSV}</span>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Filter className="w-5 h-5 text-[#1086a4] mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Export Submissions to CSV</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Submission Status</label>
                <div className="flex flex-wrap gap-2">
                  {['all', 'pending', 'approved', 'rejected'].map(status => {
                    const isSelected = selectedStatus === status;
                    const statusConfig = {
                      all: { color: 'bg-gray-100 text-gray-800', label: 'All Statuses' },
                      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
                      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
                      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
                    };

                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isSelected
                            ? 'bg-[#1086a4] text-white shadow-sm ring-2 ring-[#1086a4] ring-opacity-30'
                            : `${statusConfig[status].color} hover:shadow-sm`
                        }`}
                      >
                        {statusConfig[status].label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Program (Optional)</label>
                {programsLoading ? (
                  <div className="flex items-center text-gray-500">
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm">Loading programs...</span>
                  </div>
                ) : (
                  <select
                    value={selectedProgramCSV}
                    onChange={handleProgramChangeCSV}
                    className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4] sm:text-sm"
                  >
                    <option value="">All Programs</option>
                    {programs.map(program => (
                      <option key={program._id} value={program._id}>{program.title}</option>
                    ))}
                  </select>
                )}
              </div>

              {selectedProgramCSV && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submission Forms</label>
                  {loadingFormsCSV ? (
                    <div className="flex items-center text-gray-500 py-4">
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm">Loading submission forms...</span>
                    </div>
                  ) : availableSubmissionFormsCSV.length > 0 ? (
                    <>
                      <div className="mb-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedSubmissionFormsCSV.length === availableSubmissionFormsCSV.length}
                            onChange={handleSelectAllFormsCSV}
                            className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Select All ({availableSubmissionFormsCSV.length})
                          </span>
                        </label>
                      </div>

                      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                        {availableSubmissionFormsCSV.map(form => (
                          <div key={form._id} className="border-b border-gray-200 last:border-b-0">
                            <label className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedSubmissionFormsCSV.includes(form._id)}
                                onChange={() => handleSubmissionFormToggleCSV(form._id)}
                                className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded flex-shrink-0"
                              />
                              <div className="ml-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-900">{form.title}</span>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {form.repeatable && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Repeatable</span>}
                                    {form.achievementImageRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Image Required</span>}
                                    {form.commentRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Comment Required</span>}
                                    {form.linkRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800">Link Required</span>}
                                    {form.automaticApproval && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Auto Approval</span>}
                                  </div>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 py-4 px-3 bg-gray-50 rounded-md">
                      No active submission forms found for this program
                    </div>
                  )}
                </div>
              )}

              {/* Date Range Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date Range Filters (Optional)
                </label>

                {getUnusedDateFields().length > 0 && (
                  <div className="flex gap-2 mb-4">
                    <select
                      value={selectedDateField}
                      onChange={(e) => setSelectedDateField(e.target.value)}
                      className="flex-1 px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4] sm:text-sm"
                    >
                      <option value="">Select a date field...</option>
                      {getUnusedDateFields().map(field => (
                        <option key={field.field} value={field.field}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddDateRange}
                      disabled={!selectedDateField}
                      className="px-4 py-2 bg-[#1086a4] text-white text-sm font-medium rounded-md hover:bg-[#0c6b7a] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      + Add Date Filter
                    </button>
                  </div>
                )}

                <div className="space-y-3">
                  {dateRanges.map((range, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={range.enabled}
                            onChange={() => handleDateRangeToggle(index)}
                            className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">{range.label}</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => handleRemoveDateRange(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {range.enabled && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              From {!range.from && <span className="text-gray-400">(any date before)</span>}
                            </label>
                            <input
                              type="date"
                              value={range.from}
                              onChange={(e) => handleDateRangeChange(index, 'from', e.target.value)}
                              className="block w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              To {!range.to && <span className="text-gray-400">(any date after)</span>}
                            </label>
                            <input
                              type="date"
                              value={range.to}
                              onChange={(e) => handleDateRangeChange(index, 'to', e.target.value)}
                              className="block w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4]"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {dateRanges.length === 0 && (
                    <div className="text-sm text-gray-500 py-4 px-3 bg-gray-50 rounded-md text-center">
                      No date filters applied. Click "Add Date Filter" to filter by date ranges.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={handleExportFiles}
                disabled={exportingFiles || exportingCSV || (selectedProgramCSV && selectedSubmissionFormsCSV.length === 0)}
                className="inline-flex items-center px-6 py-3 bg-[#054581] text-white font-medium rounded-lg hover:bg-[#043a6e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingFiles ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FolderArchive className="w-5 h-5 mr-2" />
                    Export Files
                  </>
                )}
              </button>
              <button
                onClick={handleExportCSV}
                disabled={exportingCSV || exportingFiles || (selectedProgramCSV && selectedSubmissionFormsCSV.length === 0)}
                className="inline-flex items-center px-6 py-3 bg-[#1086a4] text-white font-medium rounded-lg hover:bg-[#0c6b7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingCSV ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FileDown className="w-5 h-5 mr-2" />
                    Export to CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'exportUsers' && (
        <div className="space-y-6">
          {errorUsers && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <XCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
              <span className="text-red-700">{errorUsers}</span>
            </div>
          )}

          {successMessageUsers && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-green-700">{successMessageUsers}</span>
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Filter className="w-5 h-5 text-[#1086a4] mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Export Users to CSV</h3>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Submission Status</label>
                <div className="flex flex-wrap gap-2">
                  {['all', 'pending', 'approved', 'rejected'].map(status => {
                    const isSelected = selectedStatusUsers === status;
                    const statusConfig = {
                      all: { color: 'bg-gray-100 text-gray-800', label: 'All Statuses' },
                      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
                      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
                      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' }
                    };

                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChangeUsers(status)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isSelected
                            ? 'bg-[#1086a4] text-white shadow-sm ring-2 ring-[#1086a4] ring-opacity-30'
                            : `${statusConfig[status].color} hover:shadow-sm`
                        }`}
                      >
                        {statusConfig[status].label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program <span className="text-red-500">*</span>
                </label>
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-600">
                    Program selection is required. The CSV columns depend on the selected program.
                  </p>
                </div>
                {programsLoading ? (
                  <div className="flex items-center text-gray-500">
                    <Loader className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm">Loading programs...</span>
                  </div>
                ) : (
                  <select
                    value={selectedProgramUsers}
                    onChange={handleProgramChangeUsers}
                    className="block w-full px-3 py-2 text-gray-900 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#1086a4] focus:border-[#1086a4] sm:text-sm"
                  >
                    <option value="">Select a program</option>
                    {programs.map(program => (
                      <option key={program._id} value={program._id}>{program.title}</option>
                    ))}
                  </select>
                )}
              </div>

              {selectedProgramUsers && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submission Forms</label>
                  {loadingFormsUsers ? (
                    <div className="flex items-center text-gray-500 py-4">
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      <span className="text-sm">Loading submission forms...</span>
                    </div>
                  ) : availableSubmissionFormsUsers.length > 0 ? (
                    <>
                      <div className="mb-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedSubmissionFormsUsers.length === availableSubmissionFormsUsers.length}
                            onChange={handleSelectAllFormsUsers}
                            className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">
                            Select All ({availableSubmissionFormsUsers.length})
                          </span>
                        </label>
                      </div>

                      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                        {availableSubmissionFormsUsers.map(form => (
                          <div key={form._id} className="border-b border-gray-200 last:border-b-0">
                            <label className="flex items-center p-3 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedSubmissionFormsUsers.includes(form._id)}
                                onChange={() => handleSubmissionFormToggleUsers(form._id)}
                                className="h-4 w-4 text-[#1086a4] focus:ring-[#1086a4] border-gray-300 rounded flex-shrink-0"
                              />
                              <div className="ml-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-gray-900">{form.title}</span>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {form.repeatable && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Repeatable</span>}
                                    {form.achievementImageRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Image Required</span>}
                                    {form.commentRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Comment Required</span>}
                                    {form.linkRequired && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-800">Link Required</span>}
                                    {form.automaticApproval && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">Auto Approval</span>}
                                  </div>
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-500 py-4 px-3 bg-gray-50 rounded-md">
                      No active submission forms found for this program
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleExportUsers}
                disabled={exportingUsers || !selectedProgramUsers || selectedSubmissionFormsUsers.length === 0}
                className="inline-flex items-center px-6 py-3 bg-[#1086a4] text-white font-medium rounded-lg hover:bg-[#0c6b7a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingUsers ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <FileDown className="w-5 h-5 mr-2" />
                    Export to CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  dataImportExport: state.dataImportExport,
  program: state.program,
  auth: state.auth
});

export default connect(mapStateToProps, {
  getCollections,
  exportData,
  importData,
  getCollectionCount,
  clearDataOperations,
  getPrograms
})(ImportExportDataTab);
